import React from 'react';
import Features from '../components/Features';
import PageHero from '../components/PageHero';
import FeatureShowcase from '../components/FeatureShowcase';
import { useLanguage } from '../contexts/LanguageContext';
import partnersImage from '../assets/screenshots/partners.png';
import proposalsImage from '../assets/screenshots/proposals.png';
import proposals2Image from '../assets/screenshots/proposals2.png';
import proposals3Image from '../assets/screenshots/proposals3.png';
import customersImage from '../assets/screenshots/customers.png';
import contracts1Image from '../assets/screenshots/contracts1.png';
import mobile1Image from '../assets/screenshots/mobile1.png';
import mobile2Image from '../assets/screenshots/mobile2.png';

const FeaturesPage: React.FC = () => {
  const { t, language } = useLanguage();

  // Showcase features with real images
  const showcaseFeatures = [
    {
      id: 'customer-management',
      title: {
        en: 'Customer Management',
        hu: 'Ügyfélkezelés',
      },
      description: {
        en: 'Keep all your customer information organized in one place. Track interactions, history, and preferences to provide personalized service. Search and filter capabilities make finding customer information quick and easy.',
        hu: 'Tartsa az összes ügyfélinformációt rendezetten egy helyen. Kövesse nyomon az interakciókat, előzményeket és preferenciákat a személyre szabott szolgáltatás nyújtásához. A keresési és szűrési lehetőségek gyorssá és egyszerűvé teszik az ügyfélinformációk megtalálását.',
      },
      imageUrl: customersImage,
      altText: {
        en: 'YouCRM Customer Management Dashboard',
        hu: 'YouCRM Ügyfélkezelési Vezérlőpult',
      },
    },
    {
      id: 'quotation-system',
      title: {
        en: 'Professional Quotation System',
        hu: 'Professzionális Árajánlat Rendszer',
      },
      description: {
        en: 'Create and send professional quotations with just a few clicks. Track quotation status and feedback to improve your conversion rates. Customize templates to match your brand identity.',
        hu: 'Készítsen és küldjön professzionális árajánlatokat néhány kattintással. Kövesse nyomon az árajánlatok státuszát és a visszajelzéseket, hogy javítsa a konverziós arányokat. Szabja személyre a sablonokat, hogy illeszkedjenek a márkaidentitásához.',
      },
      imageUrl: proposalsImage,
      altText: {
        en: 'YouCRM Quotation Management Interface',
        hu: 'YouCRM Árajánlat Kezelési Felület',
      },
    },
    {
      id: 'quote-creation',
      title: {
        en: 'Detailed Quote Creation',
        hu: 'Részletes Árajánlat Készítés',
      },
      description: {
        en: 'Add line items, quantities, and pricing details to your quotes quickly. The intuitive interface makes it easy to create comprehensive quotes that meet your clients\' specific needs.',
        hu: 'Adjon tételeket, mennyiségeket és áradatokat gyorsan az árajánlataihoz. Az intuitív felület megkönnyíti az olyan átfogó ajánlatok létrehozását, amelyek megfelelnek az ügyfelek egyedi igényeinek.',
      },
      imageUrl: proposals3Image,
      altText: {
        en: 'YouCRM Quote Creation Form',
        hu: 'YouCRM Árajánlat Készítési Űrlap',
      },
    },
    {
      id: 'quote-preview',
      title: {
        en: 'Professional Quote Preview',
        hu: 'Professzionális Árajánlat Előnézet',
      },
      description: {
        en: 'Preview your quotes before sending them to ensure they look professional and contain all necessary information. YouCRM generates clean, well-formatted documents that represent your brand effectively.',
        hu: 'Tekintse meg az árajánlatokat küldés előtt, hogy megbizonyosodjon arról, hogy professzionálisan néznek ki és tartalmazzák az összes szükséges információt. A YouCRM tiszta, jól formázott dokumentumokat generál, amelyek hatékonyan képviselik az Ön márkáját.',
      },
      imageUrl: proposals2Image,
      altText: {
        en: 'YouCRM Quote Document Preview',
        hu: 'YouCRM Árajánlat Dokumentum Előnézet',
      },
    },
    {
      id: 'mobile-access',
      title: {
        en: 'Mobile-Friendly Interface',
        hu: 'Mobilbarát Felület',
      },
      description: {
        en: 'Access all features from your mobile devices. Work as efficiently on your phone or tablet as you would on a desktop computer. Stay connected with your business data anywhere, anytime.',
        hu: 'Férjen hozzá minden funkcióhoz mobil eszközeiről. Dolgozzon ugyanolyan hatékonyan telefonján vagy táblagépén, mint asztali számítógépén. Maradjon kapcsolatban üzleti adataival bárhol, bármikor.',
      },
      imageUrl: mobile1Image,
      altText: {
        en: 'YouCRM Mobile Interface',
        hu: 'YouCRM Mobilbarát Felület',
      },
    },
    {
      id: 'mobile-product-management',
      title: {
        en: 'Mobile Product Management',
        hu: 'Mobil Termékkezelés',
      },
      description: {
        en: 'Manage your products and services on the go. Add, edit, and track your inventory directly from your mobile device with the same powerful features available on desktop.',
        hu: 'Kezelje termékeit és szolgáltatásait útközben is. Adjon hozzá, szerkesszen és kövesse nyomon készletét közvetlenül mobileszközéről, az asztali verzióban elérhető összes funkcióval.',
      },
      imageUrl: mobile2Image,
      altText: {
        en: 'YouCRM Mobile Product Management',
        hu: 'YouCRM Mobil Termékkezelés',
      },
    },
    {
      id: 'document-management',
      title: {
        en: 'Contract Management',
        hu: 'Szerződéskezelés',
      },
      description: {
        en: 'Store and manage customer-specific contracts securely. Easily create, store, and track contracts, agreements, and other documents. Version control ensures you always have access to the latest document versions.',
        hu: 'Tárolja és kezelje biztonságosan az ügyfelekhez kapcsolódó szerződéseket. Könnyedén hozhat létre, tárolhat és követhet szerződéseket, megállapodásokat és egyéb dokumentumokat. A verziókövetés biztosítja, hogy mindig a legfrissebb dokumentumverziókhoz férjen hozzá.',
      },
      imageUrl: contracts1Image,
      altText: {
        en: 'YouCRM Contract Management System',
        hu: 'YouCRM Szerződéskezelő Rendszer',
      },
    },
    {
      id: 'modern-crm',
      title: {
        en: 'Modern Customer Management',
        hu: 'Modern Ügyfélkezelés',
      },
      description: {
        en: 'YouCRM provides a modern approach to customer relationship management suited for today\'s business challenges. Our platform combines intuitive design with powerful functionality to streamline your customer interactions.',
        hu: 'A YouCRM modern megközelítést kínál az ügyfélkapcsolat-kezeléshez, amely alkalmazkodik a mai üzleti kihívásokhoz. Platformunk intuitív dizájnt és hatékony funkciókat egyesít az ügyfélkapcsolatok egyszerűsítése érdekében.',
      },
      imageUrl: partnersImage,
      altText: {
        en: 'YouCRM Modern Customer Management',
        hu: 'YouCRM Modern Ügyfélkezelés',
      },
      isPortrait: true, // Mark this image as portrait orientation
    },
  ];

  return (
    <div>
      <PageHero 
        title={t('nav.features')} 
        subtitle={language === 'en' 
          ? 'Discover the powerful capabilities of YouCRM'
          : 'Fedezze fel a YouCRM hatékony képességeit'
        } 
      />
      
      {/* Feature list cards */}
      <Features />
      
      {/* Feature showcase with screenshots */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            {language === 'en' ? 'Experience the Quality' : 'Tapasztalja meg a Minőséget'}
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-500">
            {language === 'en' 
              ? 'See how YouCRM transforms your business operations with its intuitive interface and powerful features'
              : 'Nézze meg, hogyan alakítja át YouCRM az üzleti működését intuitív felületével és hatékony funkcióival'
            }
          </p>
        </div>
      </div>
      
      <FeatureShowcase features={showcaseFeatures} />
    </div>
  );
};

export default FeaturesPage;